// BulkStrategyManagement.js - Replace the existing component with this updated version

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  alpha,
  Container,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import StrategyTable from './components/StrategyTable';
import StrategyForm from './components/StrategyForm';
import ConfirmDialog from './components/ConfirmDialog';

const BulkStrategyManagement = ({ apiBaseUrl = window.location.origin }) => {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [editFormData, setEditFormData] = useState(null); // ADD THIS STATE

  const loadStrategies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const endpoint = showInactive 
        ? `${apiBaseUrl}/strategies/api/bulk-strategy/list-all/?include_inactive=true`
        : `${apiBaseUrl}/strategies/api/bulk-strategy/list/`;
        
      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.bulk_strategies) {
        setStrategies(data.bulk_strategies);
      }
    } catch (err) {
      console.error('Failed to load strategies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, showInactive]);

  useEffect(() => {
    loadStrategies();
  }, [loadStrategies]);

  const handleCreate = () => {
    setFormMode('create');
    setEditFormData(null); // Clear any previous edit data
    setOpenFormDialog(true);
  };

  const handleEdit = async (strategy) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('1. Editing strategy:', strategy.bulk_strategy_id);
      
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategy/detail/${strategy.bulk_strategy_id}/`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch strategy details: HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('2. Raw backend response:', data);
      
      if (!data.success || !data.bulk_strategy) {
        throw new Error('Invalid response from server');
      }

      const strategyData = data.bulk_strategy;
      const config = strategyData.configuration || {};
      
      console.log('3. Configuration from backend:', config);
      console.log('4. Sub-strategies from backend:', config.sub_strategies);
      
      // Transform backend data to match form structure
      const formData = {
        bulk_strategy_id: strategyData.bulk_strategy_id,
        symbol: strategyData.symbol || 'NQ',
        timeframe: strategyData.timeframe || '1m',
        description: strategyData.description || '',
        exit_signal: config.exit_signal || '',
        validity_window_minutes: config.validity_window_minutes || 2880000,
        confirmation_strategies: config.confirmation_strategies || [],
        required_strategies: config.required_strategies || [],
        confirmation_time_window_seconds: config.confirmation_time_window_seconds || 5,
        allow_early_confirmation_refresh: config.allow_early_confirmation_refresh ?? true,
        sub_strategies: config.sub_strategies || [],
        active_time_enabled: config.active_time_enabled || false,
        active_start_time: config.active_start_time || '08:00',
        active_end_time: config.active_end_time || '10:00',
      };

      console.log('5. Transformed formData:', formData);
      console.log('6. FormData sub_strategies:', formData.sub_strategies);
      
      setFormMode('edit');
      setEditFormData(formData);
      setOpenFormDialog(true);
      setLoading(false);
      
    } catch (err) {
      console.error('Error loading strategy for edit:', err);
      setError(`Failed to load strategy: ${err.message}`);
      setLoading(false);
    }
  };

  const handleView = (strategy) => {
    setSelectedStrategy(strategy);
    setOpenViewDialog(true);
  };

  const handleDeleteClick = (strategy) => {
    setStrategyToDelete(strategy);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!strategyToDelete) return;

    try {
      setSubmitting(true);

      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategy/delete/${strategyToDelete.bulk_strategy_id}/`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        await new Promise(resolve => setTimeout(resolve, 300));

        setOpenDeleteDialog(false);
        setStrategyToDelete(null);

        await loadStrategies();
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to delete strategy');
      }
    } catch (err) {
      console.error('Error deleting strategy:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setStrategyToDelete(null);
  };

  const handleRefresh = async (strategy) => {
    try {
      setSubmitting(true);
      
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategy/refresh/${strategy.bulk_strategy_id}/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        await loadStrategies();
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to refresh strategy');
      }
    } catch (err) {
      console.error('Error refreshing strategy:', err);
      setError(`Failed to refresh strategy: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (strategy) => {
    try {
      setSubmitting(true);
      
      const action = strategy.is_active === false ? 'activate' : 'deactivate';
      
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategy/toggle-active/${strategy.bulk_strategy_id}/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        await loadStrategies();
        setError(null);
      } else {
        throw new Error(result.error || `Failed to ${action} strategy`);
      }
    } catch (err) {
      console.error(`Error toggling strategy active state:`, err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (payloadFromForm) => {
    try {
      setSubmitting(true);

      console.log('📤 Sending payload to backend:', JSON.stringify(payloadFromForm, null, 2));

      const response = await fetch(
        `${apiBaseUrl}/strategies/api/bulk-strategy/setup/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadFromForm),
        }
      );

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Backend error:', errorData);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Backend response:', result);

      if (result.success) {
        setOpenFormDialog(false);
        setEditFormData(null); // Clear edit data
        await loadStrategies();
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to save strategy');
      }
    } catch (err) {
      console.error('❌ Error saving strategy:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseForm = () => {
    setOpenFormDialog(false);
    setEditFormData(null); // Clear edit data when closing
  };

  const activeStrategiesCount = strategies.filter(s => s?.current_state?.in_position && s?.is_active !== false).length;
  const totalActiveStrategies = strategies.filter(s => s?.is_active !== false).length;
  const totalStrategiesCount = strategies?.length || 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0f1419',
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.03) 0%, transparent 50%)',
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3.5}>
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: '-0.5px',
                    mb: 0.5,
                  }}
                >
                  Bulk Strategy Manager
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  Create and manage multi-condition trading strategies
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={showInactive ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  onClick={() => setShowInactive(!showInactive)}
                  sx={{
                    borderColor: showInactive ? 'rgba(255, 71, 87, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                    color: showInactive ? '#ff4757' : 'rgba(255, 255, 255, 0.5)',
                    fontWeight: 600,
                    fontSize: '12px',
                    textTransform: 'none',
                    borderRadius: 0.75,
                    px: 2.5,
                    '&:hover': {
                      borderColor: showInactive ? 'rgba(255, 71, 87, 0.5)' : 'rgba(255, 255, 255, 0.4)',
                      bgcolor: showInactive ? 'rgba(255, 71, 87, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={loadStrategies}
                  disabled={loading}
                  sx={{
                    bgcolor: 'rgba(118, 75, 162, 0.8)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '12px',
                    textTransform: 'none',
                    borderRadius: 0.75,
                    px: 2.5,
                    transition: 'all 150ms',
                    '&:hover': {
                      bgcolor: 'rgba(118, 75, 162, 1)',
                    },
                    '&:disabled': {
                      opacity: 0.5,
                    },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreate}
                  sx={{
                    bgcolor: '#00ff88',
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '12px',
                    textTransform: 'none',
                    borderRadius: 0.75,
                    px: 2.5,
                    transition: 'all 150ms',
                    '&:hover': {
                      opacity: 0.85,
                    },
                  }}
                >
                  Create Strategy
                </Button>
              </Stack>
            </Stack>
          </Box>

          {totalStrategiesCount > 0 && (
            <Paper
              sx={{
                bgcolor: 'rgba(0, 212, 255, 0.04)',
                border: '1px solid rgba(0, 212, 255, 0.15)',
                borderRadius: 1.5,
                p: 2.5,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                      Active Positions
                    </Typography>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#00ff88' }}>
                      {activeStrategiesCount} / {totalActiveStrategies}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={3}>
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                      Total Strategies
                    </Typography>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#00d4ff' }}>
                      {totalStrategiesCount}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={3}>
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                      Active
                    </Typography>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: totalActiveStrategies > 0 ? '#00ff88' : 'rgba(255, 255, 255, 0.3)' }}>
                      {totalActiveStrategies}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={3}>
                  <Stack spacing={1}>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                      Inactive
                    </Typography>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: totalStrategiesCount - totalActiveStrategies > 0 ? '#ff4757' : 'rgba(255, 255, 255, 0.3)' }}>
                      {totalStrategiesCount - totalActiveStrategies}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          )}

          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              icon={<InfoIcon />}
              sx={{
                bgcolor: 'rgba(255, 71, 87, 0.1)',
                borderColor: 'rgba(255, 71, 87, 0.3)',
                color: '#ff4757',
                borderRadius: 1.5,
                '& .MuiAlert-message': {
                  fontSize: '13px',
                },
              }}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 8,
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <CircularProgress
                  sx={{ color: '#764ba2' }}
                  size={40}
                />
                <Typography
                  sx={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.5)',
                  }}
                >
                  Loading strategies...
                </Typography>
              </Stack>
            </Box>
          ) : (
            <StrategyTable
              strategies={strategies}
              loading={loading}
              onEdit={handleEdit}
              onView={handleView}
              onDelete={handleDeleteClick}
              onRefresh={handleRefresh}
              onToggleActive={handleToggleActive}
              submitting={submitting}
              showInactive={showInactive}
            />
          )}
        </Stack>
      </Container>

      {/* Pass editFormData as initialData to StrategyForm */}
      <StrategyForm
        open={openFormDialog}
        mode={formMode}
        submitting={submitting}
        initialData={editFormData} // PASS THE EDIT DATA HERE
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
      />

      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(15, 20, 25, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px)',
            borderRadius: 1.5,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'rgba(26, 31, 46, 0.5)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            pb: 2,
            pt: 2.5,
            px: 3,
          }}
        >
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
            Strategy Details: {selectedStrategy?.bulk_strategy_id}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'rgba(15, 20, 25, 0.8)', pt: 3, px: 3 }}>
          {selectedStrategy && (
            <Stack spacing={3}>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00d4ff', mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Configuration
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Symbol', value: selectedStrategy?.symbol },
                    { label: 'Timeframe', value: selectedStrategy?.timeframe },
                    { label: 'Exit Signal', value: selectedStrategy?.configuration?.exit_signal },
                    { label: 'Validity Window', value: selectedStrategy?.configuration?.validity_window_minutes ? `${selectedStrategy.configuration.validity_window_minutes}m` : '-' },
                    { label: 'Status', value: selectedStrategy?.is_active === false ? 'Inactive' : 'Active' },
                    { label: 'In Position', value: selectedStrategy?.current_state?.in_position ? 'Yes' : 'No' },
                  ].map((item, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', fontWeight: 600, mb: 0.5, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: '#fff', fontWeight: 500 }}>
                        {item.value || '-'}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00d4ff', mb: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Sub-Strategies
                </Typography>
                <Stack spacing={1}>
                  {(selectedStrategy?.sub_strategies || []).map((sub, idx) => (
                    <Paper
                      key={idx}
                      sx={{
                        p: 2,
                        bgcolor: alpha(sub?.is_completed ? '#00ff88' : '#fff', 0.04),
                        border: `1px solid ${alpha(sub?.is_completed ? '#00ff88' : '#00d4ff', 0.15)}`,
                        borderRadius: 1,
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#00d4ff' }}>
                            {sub?.strategy_id || '-'}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            {sub?.is_confirmation && (
                              <Box sx={{ fontSize: '11px', fontWeight: 700, color: '#764ba2', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Confirmation
                              </Box>
                            )}
                            {sub?.is_completed && (
                              <Box sx={{ fontSize: '11px', fontWeight: 700, color: '#00ff88', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                ✓ Completed
                              </Box>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'rgba(15, 20, 25, 0.6)', borderTop: '1px solid rgba(255, 255, 255, 0.06)', p: 2.5 }}>
          <Button
            onClick={() => setOpenViewDialog(false)}
            variant="contained"
            sx={{
              bgcolor: 'rgba(118, 75, 162, 0.8)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              textTransform: 'none',
              borderRadius: 0.75,
              '&:hover': {
                bgcolor: 'rgba(118, 75, 162, 1)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDeleteDialog}
        title="Delete Strategy"
        message={
          strategyToDelete
            ? `Delete strategy ${strategyToDelete.bulk_strategy_id}? This action will remove all sub-strategies and cannot be undone.`
            : ''
        }
        subMessage="Confirmation required"
        confirmText="Delete Strategy"
        cancelText="Cancel"
        severity="error"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={submitting}
        requiresTypedConfirmation={true}
      />
    </Box>
  );
};

export default BulkStrategyManagement;

